import React, { useState, useRef } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { formatProductsForBackend } from "../addProductwithAuto/AIProductAdd/AddProductByAI";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { addBulkProductToWarehouseUrl, bulkProductCreateUrl } from "../../../../api/endpoints";
import { setWarehouse } from "../../../../slice/warehouseSlice";
import { getAllData, saveData } from "../../../../tools/indexdb";
import { setUser } from "../../../../slice/webStoreUserSlice";
import { ButtonLoader } from "../../../../components/uxCollections/Loaders";
import { uiAlert } from "../../../../components/alerts/customAlert/uiAlert";

const AddStockViaExcel = ({ changePage, warehouse = false, setAlertDisplay }) => {
  const [currentStep, setCurrentStep] = useState("upload"); // 'upload', 'processing', 'review'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedProducts, setExtractedProducts] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const { wareHouseID } = useParams();
  const dispatch = useDispatch();
  const { store } = useSelector((state) => state.store);
  const user = useSelector((state) => state.webStoreUser.user);
  const employee = useSelector((state) => state.employee.employee);
  const currency = user.userCountryCurrencySymbol || "₦";

  // Simulated file processing - replace with actual parsing logic
  const processFile = (file) => {
    setCurrentStep("processing");
    setFileError(null);

    // Validate file type
    const validTypes = ["text/csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
      setFileError("Invalid file type. Please upload a CSV or Excel file.");
      setCurrentStep("upload");
      return;
    }
    let extractedProducts = [];
    // checking which type of file is uploaded
    const extension = file.name.split(".").pop();
    if (extension === "csv") {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          console.log(results.data);
          extractedProducts = transformProducts(results.data);
        },
      });
    } else if (extension === "xlsx" || extension === "xls") {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const workbook = XLSX.read(evt.target.result, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        console.log(XLSX.utils.sheet_to_json(sheet));
        extractedProducts = transformProducts(XLSX.utils.sheet_to_json(sheet));
      };
      reader.readAsArrayBuffer(file);
    }

    // Simulate file processing delay
    setTimeout(() => {
      setExtractedProducts(extractedProducts);
      toast.success(`${extractedProducts.length} products extracted successfully!`);
      setCurrentStep("review");
    }, 2500);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      processFile(file);
    }
  };

  const handleProductEdit = (id, field, value) => {
    setExtractedProducts((products) => products.map((product) => (product.id === id ? { ...product, [field]: value } : product)));
  };

  const handleApprove = async () => {
    uiAlert(`✅ ${extractedProducts.length} products approved!\n\nProducts will be added to your catalog.\nYou can edit them anytime from the products page.`);
    // Navigate to products page or reset
    console.log("Approved Products:", extractedProducts);

    setIsLoading(true);
    const endpoint = warehouse ? addBulkProductToWarehouseUrl + wareHouseID : bulkProductCreateUrl + store._id;
    try {
      const formattedProducts = formatProductsForBackend(
        extractedProducts.filter((p) => p.name !== "not provided"),
        warehouse
      );
      console.log("Formatted Products for Backend:", formattedProducts);
      let response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ products: formattedProducts, employee }),
      });
      console.log(response, "response after adding products");

      if (!response.ok) {
        let errorData = await response.json();
        console.log("Error response from server:", errorData);
        throw new Error("Failed to add products");
      }

      if (response.status === 207) {
        let errorData = await response.json();
        console.log("Partial success response from server:", errorData);
        setAlertDisplay({
          showAlert: true,
          type: "warning",
          title: "Partial Success",
          message: `Some products were not added: ${errorData.message || "Unknown error"} only ${errorData.insertedCount || 0} products were added.`,
        });
        return;
      }
      // Success
      //if warehouse update

      if (warehouse) {
        console.log("updating warehouse store");

        let updatedWarehouse = await response.json();
        console.log(updatedWarehouse, "updated warehouse after adding products");

        warehouse && dispatch(setWarehouse(updatedWarehouse));
        warehouse && changePage("warehouseStock");
      } else {
        const { storeCollections, user } = await response.json();
        console.log(storeCollections, "updated store collections");

        // update index dB
        let storeData = await getAllData(store.storeName);

        if (storeData) {
          storeData.storeProductCollection = storeCollections.products;

          await saveData(store.storeName, storeData);
          dispatch(setUser(user));
          changePage("stockManagement");
        }
      }

      toast.success("Products Added", `${formattedProducts.length} products have been added to your inventory.`);
    } catch (error) {
      console.error("Error adding products:", error);
      setIsLoading(false);
      setAlertDisplay({
        showAlert: true,
        type: "error",
        title: "Error",
        message: "There was an error adding the products. Please try again.",
      });
      return;
    }
  };

  const handleReupload = () => {
    setCurrentStep("upload");
    setUploadedFile(null);
    setExtractedProducts([]);
    setFileError(null);
  };

  const handleDownloadTemplate = () => {
    // Create CSV template
    const csvContent =
      "Product Name,Brand,Quantity,Cost Price,Unit Price,Category\n" +
      "Indomie Noodles,Indomie,50,80,100,Food & Beverages\n" +
      "Golden Penny Flour,Golden Penny,20,450,550,Food & Beverages";

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "storelense_product_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Upload Step
  if (currentStep === "upload") {
    return (
      <div style={{ padding: "10px 2px", background: "#f5f7fa", minHeight: "100vh", display: "flex", alignItems: "center", flexDirection: "column" }}>
        <div style={{ width: "100%", margin: "0 auto", padding: "0 4px", flex: 1 }}>
          {/* Header */}
          <div style={{ marginBottom: "24px" }}>
            <button
              style={{
                background: "transparent",
                border: "none",
                color: "#6b7280",
                fontSize: "14px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 0",
                marginBottom: "12px",
              }}
              onClick={() => changePage("stockManagement")}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <line x1='19' y1='12' x2='5' y2='12'></line>
                <polyline points='12 19 5 12 12 5'></polyline>
              </svg>
              Back
            </button>
            <h1 style={{ fontSize: "clamp(18px, 4vw, 24px)", fontWeight: "600", color: "#1a1a1a", margin: "0 0 8px 0" }}>Upload Product File</h1>
            <p style={{ fontSize: "clamp(13px, 3vw, 15px)", color: "#6b7280", margin: 0 }}>Import multiple products from CSV or Excel files</p>
          </div>

          {/* Template Download Section */}
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "clamp(16px, 4vw, 24px)",
              marginBottom: "20px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#059669' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-15a2 2 0 0 1 2-2h11l5 5v5' />
                <polyline points='17 3 17 8 22 8' />
                <line x1='12' y1='18' x2='12' y2='12' />
                <line x1='9' y1='15' x2='15' y2='15' />
              </svg>
              <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#1a1a1a", margin: 0 }}>Need a Template?</h2>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "20px",
                alignItems: "start",
              }}
            >
              <div>
                <p style={{ fontSize: "14px", color: "#6b7280", lineHeight: "1.6", marginBottom: "16px" }}>
                  Download our template to ensure your file has the correct format with all required columns:
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
                  {["Product Name (required)", "Brand", "Quantity (required)", "Cost Price (required)", "Unit Price (required)", "Category"].map(
                    (item, index) => (
                      <div key={index} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: "#059669",
                          }}
                        />
                        <span style={{ fontSize: "13px", color: "#374151" }}>{item}</span>
                      </div>
                    )
                  )}
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  style={{
                    padding: "10px 20px",
                    border: "2px solid #059669",
                    borderRadius: "8px",
                    background: "white",
                    color: "#059669",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "#059669";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "white";
                    e.currentTarget.style.color = "#059669";
                  }}
                >
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                    <polyline points='7 10 12 15 17 10' />
                    <line x1='12' y1='15' x2='12' y2='3' />
                  </svg>
                  Download CSV Template
                </button>
              </div>

              {/* Example Preview */}
              <div
                style={{
                  border: "2px dashed #e5e7eb",
                  borderRadius: "12px",
                  padding: "16px",
                  background: "#f9fafb",
                }}
              >
                <div
                  style={{
                    background: "white",
                    padding: "12px",
                    borderRadius: "8px",
                    fontFamily: "monospace",
                    fontSize: "11px",
                    lineHeight: "1.6",
                    color: "#1a1a1a",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    overflowX: "auto",
                  }}
                >
                  <div style={{ whiteSpace: "nowrap" }}>
                    <div style={{ fontWeight: "600", marginBottom: "8px", color: "#059669" }}>Product Name,Brand,Qty,Cost,Price</div>
                    <div>Indomie Noodles,Indomie,50,80,100</div>
                    <div>Golden Penny,Flour,20,450,550</div>
                    <div>Peak Milk,Peak,35,350,400</div>
                  </div>
                </div>
                <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "12px", textAlign: "center" }}>Example CSV format</p>
              </div>
            </div>
          </div>

          {/* Upload Section */}
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "clamp(20px, 5vw, 32px)",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#1a1a1a", marginBottom: "20px" }}>Upload Your File</h2>

            {/* Error Message */}
            {fileError && (
              <div
                style={{
                  padding: "14px 16px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#ef4444' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                  <circle cx='12' cy='12' r='10' />
                  <line x1='12' y1='8' x2='12' y2='12' />
                  <line x1='12' y1='16' x2='12.01' y2='16' />
                </svg>
                <span style={{ fontSize: "14px", color: "#dc2626", fontWeight: "500" }}>{fileError}</span>
              </div>
            )}

            {/* Upload Area */}
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: "3px dashed #e5e7eb",
                borderRadius: "16px",
                padding: "48px 24px",
                background: "#fafbfc",
                cursor: "pointer",
                transition: "all 0.3s ease",
                textAlign: "center",
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = "#2563eb";
                e.currentTarget.style.background = "#f8faff";
              }}
              onDragLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.background = "#fafbfc";
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.background = "#fafbfc";
                const file = e.dataTransfer.files[0];
                if (file) {
                  setUploadedFile(file);
                  processFile(file);
                }
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = "#2563eb";
                e.currentTarget.style.background = "#f8faff";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.background = "#fafbfc";
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "16px",
                  background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px",
                }}
              >
                <svg width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='#2563eb' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                  <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                  <polyline points='17 8 12 3 7 8' />
                  <line x1='12' y1='3' x2='12' y2='15' />
                </svg>
              </div>
              <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#1a1a1a", marginBottom: "8px" }}>Click to upload or drag and drop</h3>
              <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "16px" }}>CSV or Excel files only</p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", flexWrap: "wrap" }}>
                <span
                  style={{
                    padding: "4px 12px",
                    background: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "#6b7280",
                    fontWeight: "500",
                  }}
                >
                  .CSV
                </span>
                <span
                  style={{
                    padding: "4px 12px",
                    background: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "#6b7280",
                    fontWeight: "500",
                  }}
                >
                  .XLSX
                </span>
                <span
                  style={{
                    padding: "4px 12px",
                    background: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "#6b7280",
                    fontWeight: "500",
                  }}
                >
                  .XLS
                </span>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type='file'
              accept='.csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv'
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />

            <div
              style={{
                marginTop: "20px",
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "8px",
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <svg
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#6b7280'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                style={{ flexShrink: 0, marginTop: "2px" }}
              >
                <circle cx='12' cy='12' r='10' />
                <line x1='12' y1='16' x2='12' y2='12' />
                <line x1='12' y1='8' x2='12.01' y2='8' />
              </svg>
              <div style={{ fontSize: "13px", color: "#6b7280", lineHeight: "1.5" }}>
                <strong style={{ color: "#374151" }}>Tips for best results:</strong>
                <br />
                • Ensure all required columns are present
                <br />
                • Use numbers only for quantity and prices
                <br />• Maximum file size: 5MB
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Processing Step
  if (currentStep === "processing") {
    return (
      <div style={{ padding: "10px 2px", background: "#f5f7fa", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "48px",
            maxWidth: "500px",
            width: "90%",
            textAlign: "center",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          {uploadedFile && (
            <div
              style={{
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "12px",
                marginBottom: "24px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='#059669' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                  <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                  <polyline points='14 2 14 8 20 8' />
                </svg>
              </div>
              <div style={{ textAlign: "left", flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1a1a1a",
                    marginBottom: "2px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {uploadedFile.name}
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>{(uploadedFile.size / 1024).toFixed(2)} KB</div>
              </div>
            </div>
          )}

          <div
            style={{
              width: "64px",
              height: "64px",
              border: "4px solid #e5e7eb",
              borderTopColor: "#2563eb",
              borderRadius: "50%",
              margin: "0 auto 24px",
              animation: "spin 1s linear infinite",
            }}
          />

          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>

          <h2 style={{ fontSize: "24px", fontWeight: "600", color: "#1a1a1a", marginBottom: "12px" }}>Processing File...</h2>
          <p style={{ fontSize: "15px", color: "#6b7280", lineHeight: "1.6" }}>Extracting product data from your file. This will only take a moment.</p>
        </div>
      </div>
    );
  }

  // Review Step - Modern Card Design
  return (
    <div style={{ padding: "10px 2px", background: "#f5f7fa", minHeight: "100vh" }}>
      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 4px" }}>
        {/* Header */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "20px 24px",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <h1 style={{ fontSize: "clamp(20px, 4vw, 24px)", fontWeight: "600", color: "#1a1a1a", margin: "0 0 6px 0" }}>Review Extracted Products</h1>
            <p style={{ fontSize: "clamp(13px, 3vw, 14px)", color: "#6b7280", margin: 0 }}>
              {extractedProducts.length} products found • Click any field to edit
            </p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            <button
              onClick={handleReupload}
              style={{
                padding: "11px 20px",
                border: "2px solid #e5e7eb",
                borderRadius: "10px",
                background: "white",
                color: "#374151",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                flex: "1",
                minWidth: "fit-content",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = "#d1d5db";
                e.currentTarget.style.background = "#f9fafb";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.background = "white";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <polyline points='1 4 1 10 7 10'></polyline>
                <path d='M3.51 15a9 9 0 1 0 2.13-9.36L1 10'></path>
              </svg>
              Upload Different File
            </button>
            <button
              onClick={handleApprove}
              disabled={isLoading}
              style={{
                padding: "11px 24px",
                border: "none",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #3b82f6, #60a5fa)",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                flex: "1",
                minWidth: "fit-content",
                boxShadow: "0 2px 8px rgba(59, 130, 246, 0.2)",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 16px rgba(59, 130, 246, 0.3)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(59, 130, 246, 0.2)";
              }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                <polyline points='20 6 9 17 4 12' />
              </svg>
              {isLoading ? <ButtonLoader /> : `Approve & Add ${extractedProducts.length} Products`}
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div
          style={{
            background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
            borderRadius: "12px",
            padding: "14px 18px",
            marginBottom: "24px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            border: "1px solid #bfdbfe",
          }}
        >
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              background: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
              <circle cx='12' cy='12' r='10' />
              <line x1='12' y1='16' x2='12' y2='12' />
              <line x1='12' y1='8' x2='12.01' y2='8' />
            </svg>
          </div>
          <span style={{ fontSize: "clamp(12px, 3vw, 14px)", color: "#1e40af", fontWeight: "500", flex: 1 }}>
            All products can be edited anytime from the Products page
          </span>
        </div>

        {/* Products Grid - Modern Card Layout */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 380px), 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          {extractedProducts.map((product, index) => (
            <div
              key={product.id}
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                border: "1px solid #f3f4f6",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.12)";
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.borderColor = "#e5e7eb";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.08)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = "#f3f4f6";
              }}
            >
              {/* Gradient Top Bar */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: "linear-gradient(90deg, #3b82f6, #60a5fa, #93c5fd)",
                }}
              />

              {/* Product Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.15)",
                    }}
                  >
                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='#3b82f6' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'></path>
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        fontWeight: "600",
                        marginBottom: "4px",
                      }}
                    >
                      Product #{index + 1}
                    </div>
                    <div
                      style={{
                        fontSize: "17px",
                        fontWeight: "700",
                        color: "#1a1a1a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        lineHeight: 1.2,
                      }}
                    >
                      {product.name}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    padding: "6px 12px",
                    background: product.unitPrice > product.costPrice ? "#dbeafe" : "#fee2e2",
                    borderRadius: "8px",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: product.unitPrice > product.costPrice ? "#1e40af" : "#dc2626",
                    whiteSpace: "nowrap",
                  }}
                >
                  {product.unitPrice > product.costPrice ? "↗ Profit" : "↘ Loss"}
                </div>
              </div>

              {/* Product Fields */}
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {/* Product Name & Brand Row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                      }}
                    >
                      Product Name
                    </label>
                    <input
                      type='text'
                      value={product.name}
                      onChange={(e) => handleProductEdit(product.id, "name", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "14px",
                        color: "#1a1a1a",
                        fontWeight: "600",
                        transition: "all 0.2s ease",
                        outline: "none",
                        background: "#fafbfc",
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = "#3b82f6";
                        e.currentTarget.style.background = "white";
                        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.background = "#fafbfc";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                      }}
                    >
                      Brand
                    </label>
                    <input
                      type='text'
                      value={product.brand}
                      onChange={(e) => handleProductEdit(product.id, "brand", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "14px",
                        color: "#6b7280",
                        fontWeight: "500",
                        transition: "all 0.2s ease",
                        outline: "none",
                        background: "#fafbfc",
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = "#3b82f6";
                        e.currentTarget.style.background = "white";
                        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.background = "#fafbfc";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>

                {/* Category - Full Width */}
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "#6b7280",
                      marginBottom: "8px",
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    Category
                  </label>

                  <select
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.background = "#fafbfc";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "#3b82f6";
                      e.currentTarget.style.background = "white";
                      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      fontSize: "14px",
                      color: "#6b7280",
                      fontWeight: "500",
                      transition: "all 0.2s ease",
                      outline: "none",
                      background: "#fafbfc",
                    }}
                    value={product.category}
                    onChange={(e) => handleProductEdit(product.id, "category", e.target.value)}
                  >
                    <option value='' disabled selected hidden>
                      Choose category
                    </option>
                    {listOfProductCategory.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantity, Cost, Price Grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
                  {/* Quantity */}
                  <div>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                      }}
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <rect x='1' y='3' width='15' height='13'></rect>
                        <path d='M16 8h5l-3 5h3l-5 8v-7h3z'></path>
                      </svg>
                      Qty
                    </label>
                    <input
                      type='number'
                      value={product.quantity}
                      onChange={(e) => handleProductEdit(product.id, "quantity", parseInt(e.target.value) || 0)}
                      style={{
                        width: "100%",
                        padding: "12px 10px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "16px",
                        color: "#1a1a1a",
                        fontWeight: "700",
                        textAlign: "center",
                        transition: "all 0.2s ease",
                        outline: "none",
                        background: "#fafbfc",
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = "#3b82f6";
                        e.currentTarget.style.background = "white";
                        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.background = "#fafbfc";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    />
                  </div>

                  {/* Cost Price */}
                  <div>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                      }}
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <line x1='12' y1='1' x2='12' y2='23'></line>
                        <path d='M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'></path>
                      </svg>
                      Cost
                    </label>
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "14px",
                          color: "#9ca3af",
                          fontWeight: "600",
                          pointerEvents: "none",
                        }}
                      >
                        ₦
                      </span>
                      <input
                        type='number'
                        value={product.costPrice}
                        onChange={(e) => handleProductEdit(product.id, "costPrice", parseFloat(e.target.value) || 0)}
                        style={{
                          width: "100%",
                          padding: "12px 10px 12px 26px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "10px",
                          fontSize: "15px",
                          color: "#ef4444",
                          fontWeight: "700",
                          transition: "all 0.2s ease",
                          outline: "none",
                          background: "#fafbfc",
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = "#ef4444";
                          e.currentTarget.style.background = "white";
                          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(239, 68, 68, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = "#e5e7eb";
                          e.currentTarget.style.background = "#fafbfc";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      />
                    </div>
                  </div>

                  {/* Unit Price */}
                  <div>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                      }}
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <path d='M20 6L9 17l-5-5'></path>
                      </svg>
                      Price
                    </label>
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "14px",
                          color: "#9ca3af",
                          fontWeight: "600",
                          pointerEvents: "none",
                        }}
                      >
                        ₦
                      </span>
                      <input
                        type='number'
                        value={product.unitPrice}
                        onChange={(e) => handleProductEdit(product.id, "unitPrice", parseFloat(e.target.value) || 0)}
                        style={{
                          width: "100%",
                          padding: "12px 10px 12px 26px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "10px",
                          fontSize: "15px",
                          color: "#10b981",
                          fontWeight: "700",
                          transition: "all 0.2s ease",
                          outline: "none",
                          background: "#fafbfc",
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = "#10b981";
                          e.currentTarget.style.background = "white";
                          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(16, 185, 129, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = "#e5e7eb";
                          e.currentTarget.style.background = "#fafbfc";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Profit/Loss Summary */}
                <div
                  style={{
                    padding: "14px 16px",
                    background:
                      product.unitPrice > product.costPrice ? "linear-gradient(135deg, #eff6ff, #dbeafe)" : "linear-gradient(135deg, #fef2f2, #fee2e2)",
                    border: `2px solid ${product.unitPrice > product.costPrice ? "#93c5fd" : "#fecaca"}`,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        background: product.unitPrice > product.costPrice ? "#3b82f6" : "#dc2626",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                        {product.unitPrice > product.costPrice ? (
                          <polyline points='23 6 13.5 15.5 8.5 10.5 1 18'></polyline>
                        ) : (
                          <polyline points='23 18 13.5 8.5 8.5 13.5 1 6'></polyline>
                        )}
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Profit per unit
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: product.unitPrice > product.costPrice ? "#1e40af" : "#dc2626",
                        }}
                      >
                        ₦{Math.abs(product.unitPrice - product.costPrice).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: product.unitPrice > product.costPrice ? "#1e40af" : "#dc2626",
                      textAlign: "right",
                    }}
                  >
                    Total: ₦{Math.abs((product.unitPrice - product.costPrice) * product.quantity).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "16px",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #fff7ed, #fed7aa)",
              borderRadius: "16px",
              padding: "20px",
              border: "2px solid #fdba74",
              boxShadow: "0 2px 8px rgba(251, 146, 60, 0.15)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: "#f97316",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                  <path d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'></path>
                </svg>
              </div>
              <div style={{ fontSize: "12px", color: "#9a3412", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Products</div>
            </div>
            <div style={{ fontSize: "28px", fontWeight: "700", color: "#7c2d12" }}>{extractedProducts.length}</div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
              borderRadius: "16px",
              padding: "20px",
              border: "2px solid #93c5fd",
              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.15)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: "#3b82f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                  <rect x='1' y='3' width='15' height='13'></rect>
                  <path d='M16 8h5l-3 5h3l-5 8v-7h3z'></path>
                </svg>
              </div>
              <div style={{ fontSize: "12px", color: "#1e3a8a", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Qty</div>
            </div>
            <div style={{ fontSize: "28px", fontWeight: "700", color: "#1e40af" }}>{extractedProducts.reduce((sum, p) => sum + p.quantity, 0)}</div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #fef2f2, #fee2e2)",
              borderRadius: "16px",
              padding: "20px",
              border: "2px solid #fca5a5",
              boxShadow: "0 2px 8px rgba(239, 68, 68, 0.15)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                  <line x1='12' y1='1' x2='12' y2='23'></line>
                  <path d='M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'></path>
                </svg>
              </div>
              <div style={{ fontSize: "12px", color: "#7f1d1d", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Cost Value</div>
            </div>
            <div style={{ fontSize: "24px", fontWeight: "700", color: "#991b1b" }}>
              ₦{extractedProducts.reduce((sum, p) => sum + p.costPrice * p.quantity, 0).toLocaleString()}
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
              borderRadius: "16px",
              padding: "20px",
              border: "2px solid #93c5fd",
              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.15)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: "#3b82f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                  <polyline points='23 6 13.5 15.5 8.5 10.5 1 18'></polyline>
                  <polyline points='23 6 18 6 18 11'></polyline>
                </svg>
              </div>
              <div style={{ fontSize: "12px", color: "#1e3a8a", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Retail Value</div>
            </div>
            <div style={{ fontSize: "24px", fontWeight: "700", color: "#1e40af" }}>
              ₦{extractedProducts.reduce((sum, p) => sum + p.unitPrice * p.quantity, 0).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStockViaExcel;
// categories-and-transform.js

// --- Expandable category list (cleaned + sorted) ---
export let listOfProductCategory = [
  "Agriculture",
  "Automotive",
  "Baby Products",
  "Beauty & Personal Care",
  "Books & Stationery",
  "Building Materials",
  "Cameras & Photography",
  "Computers & Laptops",
  "Decorations",
  "DIY & Tools",
  "Drinks & Water",
  "Electronics",
  "Fashion",
  "Fitness & Gym Equipment",
  "Food & Beverages",
  "Furniture",
  "Gaming",
  "Grocery",
  "Health and Beauty",
  "Home and Office",
  "Home Appliances",
  "Jewelleries",
  "Kitchen Appliances",
  "Kitchen Utensils",
  "Lightnings & Wiring",
  "Luggage & Bags",
  "Medical Supplies",
  "Mobile Phones",
  "Motor Spare Parts",
  "Music Instruments",
  "Outdoor & Garden",
  "Paints & Brush",
  "PC & Desktops",
  "Pet Supplies",
  "Phone Accessories",
  "Phones and Tablets",
  "Security & Surveillance",
  "Sex Toys",
  "Shoes & Footwear",
  "Smart Home Devices",
  "Software",
  "Sporting Goods",
  "Toys and Games",
  "Watches",
  "Others", // default fallback
]
  .map((s) => s.trim())
  .filter(Boolean);

// ensure sorted and unique
listOfProductCategory = Array.from(new Set(listOfProductCategory)).sort();

/* ------------------------
       Helpers for normalization
       ------------------------ */

// normalize a string for comparisons
const norm = (s = "") => String(s).trim().toLowerCase().replace(/\s+/g, " ");

// convert to Title Case (keeps small words capitalized too for simplicity)
const titleCase = (s = "") =>
  String(s)
    .trim()
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(" ");

/*
      synonymsMap:
      common variants you want to canonicalize explicitly.
      Add more pairs here as you discover patterns in uploads.
    */
const synonymsMap = {
  "phone accessories": "Phone Accessories",
  "phone accessory": "Phone Accessories",
  "phones & accessories": "Phone Accessories",
  "phones & accessories": "Phone Accessories",
  "phones and accessories": "Phone Accessories",
  "phones & tablets": "Phones and Tablets",
  "phones and tablets": "Phones and Tablets",
  "mobile phones": "Phones and Tablets",
  mobiles: "Phones and Tablets",
  pc: "PC & Desktops",
  pcs: "PC & Desktops",
  computer: "Computers & Laptops",
  computers: "Computers & Laptops",
  "health & beauty": "Health and Beauty",
  beauty: "Beauty & Personal Care",
  food: "Food & Beverages",
  "grocery & gourmet food": "Grocery",
  "home & kitchen": "Home and Office",
  "home & office": "Home and Office",
  "sporting goods": "Sporting Goods",
  sports: "Sporting Goods",
  jewellery: "Jewelleries",
  jewelry: "Jewelleries",
  "lights & wiring": "Lightnings & Wiring",
  "lighting & wiring": "Lightnings & Wiring",
  "phone accessories": "Phone Accessories",
  "motor spare parts": "Motor Spare Parts",
  "auto parts": "Automotive",
  auto: "Automotive",
  baby: "Baby Products",
  toys: "Toys and Games",
  games: "Toys and Games",
  others: "Others",
};

// try to find best match in existing category list
function findBestCategoryMatch(candidate) {
  if (!candidate) return null;
  const c = norm(candidate);

  // 1) synonyms map exact match
  if (c in synonymsMap) return synonymsMap[c];

  // 2) exact case-insensitive match to existing categories
  const exact = listOfProductCategory.find((cat) => norm(cat) === c);
  if (exact) return exact;

  // 3) includes-match: if candidate contains a category word or vice-versa
  // score each existing category by token overlap
  const candidateTokens = new Set(c.split(/\s|&|,|-/).filter(Boolean));
  let best = null;
  let bestScore = 0;

  for (const cat of listOfProductCategory) {
    const catNorm = norm(cat);
    const catTokens = new Set(catNorm.split(/\s|&|,|-/).filter(Boolean));
    let score = 0;
    for (const t of candidateTokens) if (catTokens.has(t)) score++;
    // boost exact substring matches
    if (catNorm.includes(c) || c.includes(catNorm)) score += 1;
    if (score > bestScore) {
      bestScore = score;
      best = cat;
    }
  }

  // require minimal confidence (>=1) to accept; otherwise return null
  return bestScore >= 1 ? best : null;
}

// Add a category to listOfProductCategory (normalized, unique, sorted)
export function addCategoryIfMissing(category) {
  const raw = String(category ?? "").trim();
  if (!raw) return listOfProductCategory;
  // try to map via synonyms/normalization
  const mapped = findBestCategoryMatch(raw) ?? titleCase(raw);
  // if mapped already exists in list, nothing to do
  if (!listOfProductCategory.some((c) => norm(c) === norm(mapped))) {
    listOfProductCategory.push(mapped);
    listOfProductCategory = Array.from(new Set(listOfProductCategory)).sort();
  }
  return listOfProductCategory;
}

/* ------------------------
       transformProducts function
       ------------------------ */

/**
 * Transform raw rows into canonical product objects.
 * - normalizes category (maps/merges/falls back to "Others")
 * - adds new categories to listOfProductCategory automatically
 *
 * @param {Array<Object>} rows - Raw objects from CSV/Excel parse
 * @returns {Array<Object>} transformed array
 */
export function transformProducts(rows = []) {
  if (!Array.isArray(rows)) return [];

  // helper: normalize key string to compare (lowercase, remove non-alphanum)
  const normalizeKey = (k = "") =>
    String(k)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  // helper: get value by any key that normalizes to one in acceptedKeys
  const getValue = (row, target) => {
    const tgt = normalizeKey(target);
    for (const k of Object.keys(row || {})) {
      if (normalizeKey(k) === tgt) return row[k];
    }
    return undefined;
  };

  // helper: convert to number or 0
  const toNumber = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    const cleaned = String(v)
      .trim()
      .replace(/[,\s₹$€£]/g, "");
    if (cleaned === "") return 0;
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  // helper: convert to non-empty string or "not provided"
  const toStringOrNotProvided = (v) => {
    if (v === null || v === undefined) return "not provided";
    const s = String(v).trim();
    return s === "" ? "not provided" : s;
  };

  return rows.map((row, idx) => {
    const rawName = getValue(row, "Product Name") ?? getValue(row, "productName") ?? getValue(row, "name");

    const rawBrand = getValue(row, "Brand") ?? getValue(row, "brand");
    const rawCategory = getValue(row, "Category") ?? getValue(row, "category") ?? getValue(row, "cat");

    const rawQuantity = getValue(row, "Quantity") ?? getValue(row, "Qty") ?? getValue(row, "quantity");

    const rawCostPrice = getValue(row, "Cost Price") ?? getValue(row, "costprice") ?? getValue(row, "cost");

    const rawUnitPrice = getValue(row, "Unit Price") ?? getValue(row, "unitprice") ?? getValue(row, "price");

    // Normalize category
    let categoryFinal;
    if (rawCategory === undefined || rawCategory === null || String(rawCategory).trim() === "") {
      categoryFinal = "Others";
    } else {
      // 1) try synonyms / existing matches
      const mapped = findBestCategoryMatch(String(rawCategory));
      if (mapped) {
        categoryFinal = mapped;
      } else {
        // 2) not found → titleCase and add to category list
        const tc = titleCase(String(rawCategory));
        addCategoryIfMissing(tc);
        categoryFinal = tc;
      }
    }

    return {
      id: idx + 1,
      name: toStringOrNotProvided(rawName),
      brand: toStringOrNotProvided(rawBrand),
      quantity: toNumber(rawQuantity),
      costPrice: toNumber(rawCostPrice),
      unitPrice: toNumber(rawUnitPrice),
      category: categoryFinal,
    };
  });
}
